/**
 * EmployeeTasksHub — landing page shown when a staff member clicks "المهام"
 * from the Employee & Supervisor Dashboard.
 *
 * TWO PRIMARY BUTTONS:
 *   1) إدراج مهمة جديدة (green) → /tasks
 *      Internal team-only tasks (message + optional attachments, no service
 *      request created, no customer/agent linked).
 *
 *   2) المهام الموكلة (purple) → /tasks/delegated
 *      Tasks delegated to the staff member by the admin from the CloudGM
 *      "إدراج مهام" hub (CUST-XXXX / AGNT-XXXX). Read-only tab from the
 *      staff's perspective (they can update via the task detail page).
 */
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Plus, Inbox, ListChecks, ArrowRight, ArrowLeft } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';

export default function EmployeeTasksHub() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const Arrow = isAr ? ArrowLeft : ArrowRight;

  const CARDS = [
    {
      id: 'internal',
      icon: Plus,
      color: '#10b981',
      route: '/tasks',
      titleAr: 'إدراج مهمة جديدة',
      titleEn: 'Create new task',
      descAr: 'مهام داخلية بين فريق العمل (رسالة + مرفقات). بدون طلبات عملاء أو وكلاء.',
      descEn: 'Internal team-only tasks (message + attachments). Not linked to customers or agents.',
      testid: 'tasks-hub-internal',
    },
    {
      id: 'delegated',
      icon: Inbox,
      color: '#8B5CF6',
      route: '/tasks/delegated',
      titleAr: 'المهام الموكلة',
      titleEn: 'Delegated tasks',
      descAr: 'المهام التي أرسلها الأدمن إليك من إدراج مهام العملاء والوكلاء (CUST- / AGNT-).',
      descEn: 'Tasks assigned to you by the admin from the manual-tasks hub (CUST- / AGNT-).',
      testid: 'tasks-hub-delegated',
    },
  ];

  return (
    <DashboardLayout
      title="المهام"
      titleEn="Tasks"
      subtitle="اختر بين إنشاء مهمة داخلية جديدة أو مراجعة المهام الموكلة إليك من الأدمن"
      subtitleEn="Create a new internal task or review tasks delegated to you by the admin"
      screenCode="EMP-TASK-HUB"
      backPath="/employee-dashboard"
      onBack={() => navigate('/employee-dashboard')}
      breadcrumb={[
        { labelAr: 'لوحة تحكم الموظف', labelEn: 'Employee Dashboard', path: '/employee-dashboard' },
        { labelAr: 'المهام', labelEn: 'Tasks' },
      ]}
    >
      <section
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
        data-testid="employee-tasks-hub"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center">
            <ListChecks className="w-6 h-6 text-[#d4af37]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">
              {isAr ? 'مركز المهام' : 'Task Center'}
            </h2>
            <p className="text-sm text-white/50">
              {isAr ? 'مهام داخلية + مهام موكلة من الإدارة' : 'Internal tasks + delegated tasks from admin'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.button
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                whileHover={{ y: -4 }}
                onClick={() => navigate(card.route)}
                className="group relative overflow-hidden rounded-2xl border transition-all p-6 text-start hover:border-white/25"
                style={{
                  borderColor: `${card.color}30`,
                  background: `linear-gradient(135deg, ${card.color}12, ${card.color}03)`,
                }}
                data-testid={card.testid}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                       style={{ backgroundColor: `${card.color}22`, border: `1px solid ${card.color}55` }}>
                    <Icon className="w-7 h-7" style={{ color: card.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">
                      {isAr ? card.titleAr : card.titleEn}
                    </h3>
                    <p className="text-sm text-white/60 leading-relaxed">
                      {isAr ? card.descAr : card.descEn}
                    </p>
                  </div>
                  <Arrow className="w-5 h-5 text-white/40 flex-shrink-0 mt-1 group-hover:text-white/70 transition-colors" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </section>
    </DashboardLayout>
  );
}
